/**
 * useLogoutListener Hook
 * Listens for logout events from other apps and triggers local logout
 * Uses MQTT to receive logout signals across apps
 */

import { useEffect } from 'react';
import { onLogoutEvent, getDeviceId, initMqttLogoutClient } from '../services/mqttLogoutService';

/**
 * Hook to listen for logout events from other apps
 * @param {Function} onLogout - Callback function to execute when logout event is received
 * @param {Boolean} enabled - Whether to enable the listener (default: true)
 */
export const useLogoutListener = (onLogout, enabled = true) => {
  useEffect(() => {
    if (!enabled) return;

    const setupListener = async () => {
      try {
        console.log('📡 Setting up logout listener...');
        
        // Initialize MQTT client if not already initialized
        console.log('🔌 Initializing MQTT client...');
        await initMqttLogoutClient();
        
        const currentDeviceId = getDeviceId();
        console.log('🔐 Listening for logout events on device:', currentDeviceId);

        // Setup listener for logout events
        const unsubscribe = onLogoutEvent((logoutEvent) => {
          console.log('🔔 Received logout event:', logoutEvent);
          
          // Check if this logout event is for our device
          if (logoutEvent.deviceId === currentDeviceId) {
            console.log('✅ Logout event is for this device, triggering logout callback');
            
            if (onLogout && typeof onLogout === 'function') {
              onLogout(logoutEvent);
            }
          } else {
            console.log('⏭️ Logout event is for different device, ignoring');
          }
        });

        // Cleanup listener on unmount
        return () => {
          console.log('🧹 Cleaning up logout listener');
          if (unsubscribe && typeof unsubscribe === 'function') {
            unsubscribe();
          }
        };
      } catch (error) {
        console.error('❌ Error setting up logout listener:', error);
      }
    };

    // Return the cleanup function
    const cleanupPromise = setupListener();
    
    return () => {
      // Cleanup when component unmounts
      if (cleanupPromise && typeof cleanupPromise.then === 'function') {
        cleanupPromise.then(cleanup => {
          if (cleanup && typeof cleanup === 'function') {
            cleanup();
          }
        });
      }
    };
  }, [onLogout, enabled]);
};

export default useLogoutListener;
